import { useEffect, useState } from 'react'
import { X, Plus, Pencil, Trash2, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import ModalPortal from '../UI/ModalPortal'
import ConfirmDialog from '../UI/ConfirmDialog'
import { useFavorites } from '../../context/FavoritesContext'
import { formatApiError } from '../../utils/formatApiError'

/**
 * Управление личными списками избранного: создать, переименовать, удалить.
 * Дефолтный список переименовать можно, удалить — нет (сервер отдаёт 409).
 */
const FavoriteListsManagerModal = ({ isOpen, onClose }) => {
  const { lists, loaded, createList, updateList, deleteList } = useFavorites()
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [pendingDelete, setPendingDelete] = useState(null)
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    setNewName('')
    setEditingId(null)
    setEditingName('')
    setPendingDelete(null)
    setCreateError('')
  }, [isOpen])

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const handleClose = () => {
    if (editingId !== null) {
      cancelEdit()
      return
    }
    onClose()
  }

  if (!isOpen) return null

  const handleCreate = async (event) => {
    event.preventDefault()
    const name = newName.trim()
    if (!name || busy) return
    setBusy(true)
    setCreateError('')
    try {
      await createList(name)
      setNewName('')
      toast.success(`List "${name}" created`)
    } catch (error) {
      // Ошибка про поле показывается у поля: дубликат имени — самый частый
      // отказ (409), и одного тоста для него мало.
      const message = formatApiError(error, 'Failed to create list')
      setCreateError(message)
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  const startEdit = (list) => {
    setEditingId(list.id)
    setEditingName(list.name)
  }

  const handleRename = async (event) => {
    event.preventDefault()
    const name = editingName.trim()
    if (!name || busy) return
    setBusy(true)
    try {
      await updateList(editingId, { name })
      setEditingId(null)
      setEditingName('')
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to rename list'))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    if (!pendingDelete) return
    setBusy(true)
    try {
      await deleteList(pendingDelete.id)
      toast.success(`List "${pendingDelete.name}" deleted`)
      setPendingDelete(null)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete list'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ModalPortal onClose={handleClose} ariaLabel="Favorite lists manager">
        <div className="flex max-h-[calc(100dvh-1.5rem)] min-h-0 w-full max-w-md flex-col overflow-hidden rounded-xl bg-light-bg-primary dark:bg-dark-bg-primary border border-light-border-primary dark:border-dark-border-primary shadow-xl sm:max-h-[calc(100dvh-2rem)]">
          <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary sm:px-5">
            <h2 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary">
              Manage favorite lists
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
            {/* До ответа сервера показываем загрузку: пустое состояние
                иначе мелькает при открытии сразу после перехода на страницу */}
            {!loaded && (
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary py-2">
                Loading…
              </p>
            )}
            {loaded && lists.length === 0 && (
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary py-2">
                No lists yet. The first one is created automatically when you star a talent.
              </p>
            )}

            <ul className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
              {lists.map(list => (
                <li key={list.id} className="flex min-w-0 flex-wrap items-center gap-2 py-2">
                  {editingId === list.id ? (
                    <form onSubmit={handleRename} className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        maxLength={120}
                        autoFocus
                        className="min-h-10 min-w-[10rem] flex-1 px-2 py-2 text-sm rounded border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary"
                      />
                      <button type="submit" disabled={busy} title="Save" className="flex h-10 w-10 items-center justify-center rounded text-primary-600 disabled:opacity-40">
                        <Check className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={cancelEdit} title="Cancel" className="flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary">
                        <X className="w-4 h-4" />
                      </button>
                    </form>
                  ) : (
                    <>
                      <span className="min-w-[8rem] flex-1 break-words text-sm text-light-text-primary dark:text-dark-text-primary">
                        {list.name}
                      </span>
                      {list.is_default && (
                        <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">default</span>
                      )}
                      <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary tabular-nums">
                        {list.items_count ?? 0}
                      </span>
                      <button
                        type="button"
                        onClick={() => startEdit(list)}
                        title="Rename"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(list)}
                        disabled={list.is_default}
                        title={list.is_default ? 'The default list cannot be deleted' : 'Delete list'}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-red-600 disabled:opacity-30 disabled:hover:text-light-text-tertiary dark:hover:bg-dark-bg-secondary"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleCreate} className="shrink-0 px-4 py-3 border-t border-light-border-primary dark:border-dark-border-primary sm:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={newName}
              onChange={(event) => { setNewName(event.target.value); if (createError) setCreateError('') }}
              placeholder="New list name..."
              maxLength={120}
              aria-invalid={!!createError}
              className={`min-h-10 min-w-0 flex-1 px-2 py-2 text-sm rounded border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary ${
                createError
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-light-border-primary dark:border-dark-border-primary'
              }`}
            />
            <button
              type="submit"
              disabled={!newName.trim() || busy}
              className="inline-flex min-h-10 w-full items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create
            </button>
            </div>
            {createError && (
              <p role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {createError}
              </p>
            )}
          </form>
        </div>
      </ModalPortal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete favorite list"
        message={pendingDelete
          ? `Delete "${pendingDelete.name}" and remove ${pendingDelete.items_count ?? 0} talent(s) from it? The talents themselves are not affected.`
          : ''}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={busy}
      />
    </>
  )
}

export default FavoriteListsManagerModal
